// Comms data layer (BUILD_BRIEF §9.4): the Call and WhatsApp buttons, the disposition prompt, the
// recording player, the shared inbox, coaching, reports and Admin → Integrations. The CRM never dials or
// sends itself — every action here goes to the configured integration (webhook or provider mode).
import { useQuery } from '@tanstack/react-query';
import { get } from '../../lib/api.js';

export type CallStatus =
  'RINGING' | 'IN_PROGRESS' | 'COMPLETED' | 'MISSED' | 'ABANDONED' | 'BUSY' | 'NO_ANSWER' | 'FAILED';
export type MessageStatus = 'RECEIVED' | 'QUEUED' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
export type RecordingState = {
  status: 'FETCHING' | 'STORED' | 'FAILED' | 'DELETED';
  durationSeconds: number | null;
  deleteAfter: string;
};

export interface CommsStatus {
  telephony: {
    enabled: boolean;
    mode: string;
    canPlace: boolean;
    agentPhoneSet: boolean;
    dispositions: string[];
  };
  messaging: {
    enabled: boolean;
    mode: string;
    canSend: boolean;
    windowHours: number;
    quietHours: { start: string; end: string } | null;
    frequencyCaps: { perDay: number | null; perWeek: number | null };
  };
  recording: { canPlay: boolean; canDownload: boolean; retentionDays: number };
}

export interface CallRow {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  status: CallStatus;
  customerNumber: string | null;
  virtualNumber: string | null;
  talkSeconds: number | null;
  ringSeconds: number | null;
  disposition: string | null;
  dispositionAt: string | null;
  occurredAt: string;
  startedAt: string | null;
  endedAt: string | null;
  provider: string;
  lead: { id: string; name: string } | null;
  agent: { id: string; displayName: string } | null;
  recording: RecordingState | null;
  training: { id: string; kind: string; title: string } | null;
  scorecardCount: number;
}

export interface Scorecard {
  id: string;
  scores: { key: string; label: string; max: number; score: number }[];
  totalScore: number;
  maxScore: number;
  comments: string | null;
  createdAt: string;
  updatedAt: string;
  reviewer: { id: string; displayName: string };
}

export interface CallDetail extends Omit<CallRow, 'training'> {
  inScope: boolean;
  canDispose: boolean;
  answeredAt: string | null;
  waitSeconds: number | null;
  hangupCause: string | null;
  hangupBy: string | null;
  ivrPath: string[];
  recordingDisclosurePlayed: boolean | null;
  dispositionNotes: string | null;
  transcript: {
    language: string | null;
    text: string;
    segments: { start?: number; end?: number; speaker?: string; text?: string }[] | null;
    keywords: string[];
  } | null;
  transcriptDeleted: boolean;
  scorecards: Scorecard[];
  training: {
    id: string;
    kind: 'good_example' | 'to_improve';
    title: string;
    note: string | null;
    tags: string[];
    createdAt: string;
    flaggedBy: { id: string; displayName: string };
  } | null;
}

export interface PendingCall {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  status: CallStatus;
  talkSeconds: number | null;
  occurredAt: string;
  endedAt: string | null;
  lead: {
    id: string;
    name: string;
    interests: { id: string; nextFollowUpAt: string | null; project: { name: string } }[];
  } | null;
}

export interface Criterion {
  key: string;
  label: string;
  max: number;
}

export interface TrainingItem {
  id: string;
  kind: 'good_example' | 'to_improve';
  title: string;
  note: string | null;
  tags: string[];
  createdAt: string;
  flaggedBy: { id: string; displayName: string };
  call: {
    id: string;
    direction: 'INBOUND' | 'OUTBOUND';
    talkSeconds: number | null;
    occurredAt: string;
    disposition: string | null;
    agent: { id: string; displayName: string } | null;
    recording: RecordingState | null;
    scorecards: { totalScore: number; maxScore: number }[];
  };
}

export interface InboxConversation {
  id: string;
  waNumber: string;
  customerNumber: string | null;
  unreadCount: number;
  lastMessageAt: string | null;
  windowExpiresAt: string | null;
  closedAt: string | null;
  assignedTo: { id: string; displayName: string } | null;
  lead: { id: string; name: string; ownerId: string | null } | null;
  consent: 'GRANTED' | 'WITHDRAWN' | 'UNKNOWN';
  last: {
    id: string;
    direction: 'INBOUND' | 'OUTBOUND';
    type: string;
    text: string | null;
    status: MessageStatus;
    occurredAt: string;
  } | null;
  window: { open: boolean; expiresAt: string | null; minutesLeft: number };
}

export interface InboxList {
  conversations: InboxConversation[];
  counts: { mine: number; mineUnread: number; unassigned: number | null };
  canSupervise: boolean;
  seesUnassigned: boolean;
}

export type ThreadItem =
  | {
      kind: 'message';
      at: string;
      id: string;
      direction: 'INBOUND' | 'OUTBOUND';
      type: string;
      text: string | null;
      media: Record<string, unknown> | null;
      template: Record<string, unknown> | null;
      status: MessageStatus;
      error: Record<string, unknown> | null;
      occurredAt: string;
      sentByName: string | null;
    }
  | {
      kind: 'note';
      at: string;
      id: string;
      noteKind: 'NOTE' | 'TRANSFER' | 'ASSIGN';
      text: string | null;
      createdAt: string;
      author: { id: string; displayName: string };
      fromMember: { id: string; displayName: string } | null;
      toMember: { id: string; displayName: string } | null;
    };

export interface Thread {
  conversation: InboxConversation;
  items: ThreadItem[];
  hasMore: boolean;
}

export interface Template {
  id: string;
  name: string;
  language: string;
  category: string | null;
  body: string;
  variables: string[];
  provider: string;
}

export interface InboxMember {
  id: string;
  displayName: string;
  team: { id: string; name: string } | null;
}

export interface SearchResult {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  text: string | null;
  status: MessageStatus;
  occurredAt: string;
  conversation: {
    id: string;
    customerNumber: string | null;
    assignedTo: { id: string; displayName: string } | null;
    lead: { id: string; name: string } | null;
  };
}

export type GroupBy = 'executive' | 'team' | 'project' | 'hour';

export interface CallReportRow {
  key: string | null;
  label: string;
  calls: number;
  inbound: number;
  outbound: number;
  answered: number;
  connectRate: number | null;
  talkSeconds: number;
  averageTalkSeconds: number | null;
  missed: number;
  abandoned: number;
  recovered: number;
  recoveryRate: number | null;
  averageRecoveryMinutes: number | null;
}

export interface MessageReportRow {
  key: string | null;
  label: string;
  conversations: number;
  inbound: number;
  outbound: number;
  templates: number;
  delivered: number;
  read: number;
  failed: number;
  deliveryRate: number | null;
  readRate: number | null;
  replied: number;
  replyRate: number | null;
  averageResponseMinutes: number | null;
}

export interface Report<Row> {
  groupBy: GroupBy;
  from: string;
  to: string;
  timezone: string;
  rows: Row[];
  total: Omit<Row, 'key' | 'label'>;
}

export interface IntegrationSettings {
  telephony: {
    mode: string;
    version: number;
    numbers: { number: string; projectId: string | null; source: string | null }[];
    businessHours: { start: string; end: string };
    missedCallSlaMinutes: number;
    recordingHosts: string[];
    disclosureRequired: boolean;
    dispositions: string[];
  };
  whatsapp: {
    mode: string;
    version: number;
    numbers: { waNumber: string; projectIds: string[]; defaultSource: string | null }[];
    quietHours: { start: string; end: string } | null;
    frequencyCaps: { perDay: number | null; perWeek: number | null };
  };
  coaching: { criteria: Criterion[] };
  recordingRetention: { effectiveDays: number; platformDays: number | null; organisationDays: number | null };
  providers: { telephony: string[]; messaging: string[] };
}

export interface ServiceToken {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  ipAllowList: string[];
  rateLimitPerSec: number;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  rateLimitPerMin: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  createdAt: string;
}

export interface Delivery {
  id: string;
  subscriptionId: string;
  eventType: string;
  status: 'PENDING' | 'DELIVERED' | 'FAILED' | 'DEAD';
  attempts: number;
  responseStatus: number | null;
  error: string | null;
  nextAttemptAt: string;
  deliveredAt: string | null;
  createdAt: string;
}

/** Scopes a service token can carry (INTEGRATION_CONTRACT §2.1). */
export const TOKEN_SCOPES = [
  ['leads:lookup', 'Look up leads by phone (screen pop, sticky agent)'],
  ['leads:write', 'Create leads from new callers and WhatsApp senders'],
  ['users:read', 'See who can take a call now'],
  ['users:phone:read', 'See members’ phone numbers (bridging, masking)'],
  ['calls:write', 'Record calls, recordings and transcripts'],
  ['tasks:write', 'Create missed-call callback tasks'],
  ['messages:write', 'Record WhatsApp messages and their status'],
  ['consents:write', 'Record opt-ins and opt-outs'],
  ['templates:write', 'Sync approved WhatsApp templates'],
  ['config:read', 'Read the telephony and WhatsApp settings'],
  ['leads:read', 'Reserved for later contract versions'],
  ['campaigns:read', 'Read WhatsApp campaign recipients to send to'],
] as const;

/** Events the telephony and WhatsApp services usually subscribe to. */
export const COMMS_EVENTS = [
  'click_to_call.requested',
  'message.send_requested',
  'campaign.whatsapp_send_requested',
  'integration_config.changed',
  'consent.changed',
  'lead.assigned',
  'conversation.assigned',
  'call.disposition_logged',
];

export const useCommsStatus = () =>
  useQuery({
    queryKey: ['comms-status'],
    queryFn: () => get<CommsStatus>('/comms/status'),
    staleTime: 60_000,
  });

export const usePendingDispositions = (enabled: boolean) =>
  useQuery({
    queryKey: ['calls', 'pending-disposition'],
    queryFn: () => get<PendingCall[]>('/calls/pending-disposition'),
    enabled,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });

export const useCall = (id: string | null) =>
  useQuery({ queryKey: ['call', id], queryFn: () => get<CallDetail>(`/calls/${id}`), enabled: Boolean(id) });

export const useCriteria = (enabled: boolean) =>
  useQuery({
    queryKey: ['coaching-criteria'],
    queryFn: () => get<Criterion[]>('/coaching/criteria'),
    enabled,
  });

export const useTemplates = (enabled: boolean) =>
  useQuery({
    queryKey: ['inbox-templates'],
    queryFn: () => get<Template[]>('/inbox/templates'),
    enabled,
    staleTime: 300_000,
  });

export const useInboxMembers = (enabled: boolean) =>
  useQuery({
    queryKey: ['inbox-members'],
    queryFn: () => get<InboxMember[]>('/inbox/members'),
    enabled,
    staleTime: 300_000,
  });

export function query(params: Record<string, string | number | undefined | null | false>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== false && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}

/** Fills a template body's {{1}}, {{2}} … placeholders for the preview. */
export function fillTemplate(body: string, values: Record<string, string>): string {
  return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k: string) => values[k]?.trim() || `{{${k}}}`);
}

/** Placeholder keys a template body uses, in order ({{1}}, {{2}} … or named). */
export function placeholders(body: string): string[] {
  return [...new Set([...body.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map((m) => m[1]!))];
}

export const CALL_STATUS_LABEL: Record<CallStatus, string> = {
  RINGING: 'Ringing',
  IN_PROGRESS: 'On the call',
  COMPLETED: 'Answered',
  MISSED: 'Missed',
  ABANDONED: 'Caller hung up',
  BUSY: 'Busy',
  NO_ANSWER: 'No answer',
  FAILED: 'Failed',
};
